import { configureStore } from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query'
import adminErrorReducer from './features/admin/errorsSlice'
import adminModalReducer from './features/admin/modalSlice'
import catalogReducer from './features/catalogSlice'
import filterReducer from './features/filterSlice'
import langReducer from './features/langSlice'
import modalReducer from './features/modalSlice'
import { authMiddleware } from './middlewares/authMiddleware'
import { animalAgesApi } from './services/admin/animalAgesApi'
import { animalNeedsApi } from './services/admin/animalNeedsApi'
import { AnimalTypeCategoriesApi } from './services/admin/animalTypeCategoryApi'
import { animalTypesApi } from './services/admin/animalTypesApi'
import { ApplicationsApi } from './services/admin/applicationApi'
import { ApplicationCategoriesApi } from './services/admin/applicationCategoriesApi'
import { applicationItemsApi } from './services/admin/applicationItemsApi'
import { CardDataApi } from './services/admin/cardDataApi'
import { CardGroupsApi } from './services/admin/cardGroupsApi'
import { CardTypesApi } from './services/admin/cardTypesApi'
import { cardApi } from './services/admin/cardsApi'
import { CompositionsApi } from './services/admin/compositionApi'
import { CompositionCategoriesApi } from './services/admin/compositionCategoriesApi'
import { CompositionItemsApi } from './services/admin/compositionItemsApi'
import { feedingTypesApi } from './services/admin/feedingTypesApi'
import { managerApi } from './services/admin/managersApi'
import { PackagingTypesApi } from './services/admin/packagingTypeApi'
import { productTypeApi } from './services/admin/productTypesApi'
import { questionApi } from './services/admin/questionApi'
import { quizApi } from './services/admin/quizApi'
import { catalogApi } from './services/catalogApi'
import { ExternalLinksApi } from './services/externalLinksApi'
import { footerApi } from './services/footerApi'
import { materialsApi } from './services/materialsApi'
import { MegaschoolReleasesApi } from './services/megaschoolReleasesApi'
import { newsApi } from './services/newsApi'
import { reviewApi } from './services/reviewApi'
import { tagsApi } from './services/tagsApi'
import { UsefulMaterialTypesApi } from './services/usefulMaterialTypesApi'
import { usersApi } from './services/usersApi'
import { vacancyApi } from './services/vacancyApi'
import { webinarApi } from './services/webinarsApi'
import { AdminUsersApi } from './services/admin/adminUsersApi'
import { catalogFeatureApi } from '@/features/catalog/api/catalogApi'
import catalogSlice from '@/features/catalog'
import { pagesApi } from '@/features/head/api/pagesApi'
import { testingApi } from './services/testingApi'
import { themeSlice } from '@/shared/model/themeSlice'

export const store = configureStore({
    reducer: {
        [materialsApi.reducerPath]: materialsApi.reducer,
        [catalogApi.reducerPath]: catalogApi.reducer,
        [newsApi.reducerPath]: newsApi.reducer,
        [tagsApi.reducerPath]: tagsApi.reducer,
        [managerApi.reducerPath]: managerApi.reducer,
        [cardApi.reducerPath]: cardApi.reducer,
        [CardTypesApi.reducerPath]: CardTypesApi.reducer,
        [productTypeApi.reducerPath]: productTypeApi.reducer,
        [animalTypesApi.reducerPath]: animalTypesApi.reducer,
        [animalAgesApi.reducerPath]: animalAgesApi.reducer,
        [feedingTypesApi.reducerPath]: feedingTypesApi.reducer,
        [animalNeedsApi.reducerPath]: animalNeedsApi.reducer,
        [applicationItemsApi.reducerPath]: applicationItemsApi.reducer,
        [ExternalLinksApi.reducerPath]: ExternalLinksApi.reducer,
        [MegaschoolReleasesApi.reducerPath]: MegaschoolReleasesApi.reducer,
        [CardDataApi.reducerPath]: CardDataApi.reducer,
        [CardGroupsApi.reducerPath]: CardGroupsApi.reducer,
        [ApplicationsApi.reducerPath]: ApplicationsApi.reducer,
        [ApplicationCategoriesApi.reducerPath]: ApplicationCategoriesApi.reducer,
        [CompositionItemsApi.reducerPath]: CompositionItemsApi.reducer,
        [CompositionsApi.reducerPath]: CompositionsApi.reducer,
        [CompositionCategoriesApi.reducerPath]: CompositionCategoriesApi.reducer,
        [AnimalTypeCategoriesApi.reducerPath]: AnimalTypeCategoriesApi.reducer,
        // [PagesApi.reducerPath]: PagesApi.reducer,
        [pagesApi.reducerPath]: pagesApi.reducer,
        [reviewApi.reducerPath]: reviewApi.reducer,
        [UsefulMaterialTypesApi.reducerPath]: UsefulMaterialTypesApi.reducer,
        [footerApi.reducerPath]: footerApi.reducer,
        [PackagingTypesApi.reducerPath]: PackagingTypesApi.reducer,
        [vacancyApi.reducerPath]: vacancyApi.reducer,
        [webinarApi.reducerPath]: webinarApi.reducer,
        [questionApi.reducerPath]: questionApi.reducer,
        [quizApi.reducerPath]: quizApi.reducer,
        [usersApi.reducerPath]: usersApi.reducer,
        [AdminUsersApi.reducerPath]: AdminUsersApi.reducer,
        [catalogFeatureApi.reducerPath]: catalogFeatureApi.reducer,
        [testingApi.reducerPath]: testingApi.reducer,
        themeSlice: themeSlice.reducer,
        adminModal: adminModalReducer,
        modal: modalReducer,
        adminError: adminErrorReducer,
        filter: filterReducer,
        // catalog: catalogReducer,
        catalog: catalogSlice,
        catalogv1: catalogReducer,
        lang: langReducer
    },
    middleware: getDefaultMiddleware =>
        getDefaultMiddleware({}).concat(
            usersApi.middleware,
            vacancyApi.middleware,
            quizApi.middleware,
            questionApi.middleware,
            webinarApi.middleware,
            catalogApi.middleware,
            newsApi.middleware,
            tagsApi.middleware,
            managerApi.middleware,
            cardApi.middleware,
            productTypeApi.middleware,
            animalTypesApi.middleware,
            animalAgesApi.middleware,
            feedingTypesApi.middleware,
            animalNeedsApi.middleware,
            applicationItemsApi.middleware,
            materialsApi.middleware,
            ExternalLinksApi.middleware,
            MegaschoolReleasesApi.middleware,
            CardTypesApi.middleware,
            CardDataApi.middleware,
            CardGroupsApi.middleware,
            ApplicationsApi.middleware,
            catalogFeatureApi.middleware,
            ApplicationCategoriesApi.middleware,
            CompositionItemsApi.middleware,
            CompositionsApi.middleware,
            CompositionCategoriesApi.middleware,
            AnimalTypeCategoriesApi.middleware,
            pagesApi.middleware,
            reviewApi.middleware,
            UsefulMaterialTypesApi.middleware,
            footerApi.middleware,
            PackagingTypesApi.middleware,
            AdminUsersApi.middleware,
            testingApi.middleware,
            authMiddleware
        ),
    devTools: process.env.NODE_ENV !== 'production'
})

setupListeners(store.dispatch)

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
